import * as Styled from "./FilterEventsModal.styles";

import { Box, Button, Divider, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
  FormatType,
  OfficeLocation,
  clearSortAndFilterEvents,
  filterAndSortEvents,
} from "../../store/actions/eventsActions";
import BaseModal from "../BaseModal/BaseModal";
import { Input } from "../../components";
import NotInterestedOutlinedIcon from "@mui/icons-material/NotInterestedOutlined";
import formOptions from "../../pages/CreateEventPage/formOptions";
import { useAppDispatch } from "../../store/hooks";
import { useEffect } from "react";

type FilterEventsModalProps = {
  open: any;
  onClose: any;
  filterPills: string[];
};

const FilterEventsModal = ({
  open,
  onClose,
  filterPills,
}: FilterEventsModalProps) => {
  const dispatch = useAppDispatch();

  const formMethods = useForm({
    defaultValues: {
      sortBy: "date-soonest",
      office: OfficeLocation.NEW_ENGLAND,
      format: FormatType.ALL,
      eventTags: ["All"],
    },
    mode: "onChange",
  });

  const sortByOptions: { label: string; value: string }[] = [
    { label: "Date (soonest)", value: "date-soonest" },
    { label: "Date (furthest out)", value: "date-furthest" },
    { label: "Title A - Z", value: "titleAscending" },
    { label: "Title Z - A", value: "titleDescending" },
    { label: "# of signups (most)", value: "mostSignups" },
    { label: "# of signups (least)", value: "leastSignups" },
  ];

  const theme = useTheme();

  const pillFormValueComparison = (formItem: string) => {
    if (filterPills) {
      if (formItem === "format") {
        const currentFormatValue = formMethods.getValues("format");
        if (
          !filterPills.includes(currentFormatValue) &&
          currentFormatValue !== (FormatType.ALL as FormatType)
        ) {
          formMethods.setValue("format", FormatType.ALL as FormatType);
        }
      }
      if (formItem === "office") {
        const currentOfficeValue = formMethods.getValues("office");
        if (
          !filterPills.includes(currentOfficeValue) &&
          currentOfficeValue !== (OfficeLocation.NEW_ENGLAND as OfficeLocation)
        ) {
          formMethods.setValue(
            "office",
            OfficeLocation.NEW_ENGLAND as OfficeLocation
          );
        }
      }
      if (formItem === "eventTags") {
        const currentEventTagsValue = formMethods.getValues("eventTags");

        const filteredEventsTags = currentEventTagsValue.filter((tags) =>
          filterPills.includes(tags)
        );
        formMethods.setValue("eventTags", filteredEventsTags);
        if (formMethods.getValues("eventTags").length < 1) {
          formMethods.setValue("eventTags", ["All"]);
        }
      }
    }
  };

  useEffect(() => {
    pillFormValueComparison("format");
    pillFormValueComparison("office");
    pillFormValueComparison("eventTags");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPills]);

  return (
    <BaseModal
      desktopWidth={800}
      drawer
      mobileWidth={"100%"}
      open={open}
      overflow="scroll"
      onClose={onClose}
    >
      <Box>
        <FormProvider {...formMethods}>
          <Styled.ModalTitleContainer>
            <Typography variant="h2">Sort & Filter Events</Typography>
          </Styled.ModalTitleContainer>
          <Box>
            <Styled.ModalSortContainer>
              <Typography variant="modalBody">Sort By:</Typography>
              <Input
                name="sortBy"
                control={formMethods.control}
                variant="pill"
                defaultValue={"date-soonest"}
                select
                selectOptions={sortByOptions}
                removeMarginTop
                sx={{
                  width: "100%",
                  [theme.breakpoints.up("desktop")]: { width: "33%" },
                }}
              />
            </Styled.ModalSortContainer>
            <Divider
              sx={{
                width: "100%",
                marginTop: theme.spacing(3),
                marginBottom: theme.spacing(3),
                backgroundColor: theme.colors.neutral.gray,
              }}
            />
            <Styled.ModalFilterContainer>
              <Typography
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "start",
                }}
                variant="modalBody"
              >
                Filter By:
              </Typography>
              <Styled.InputStack>
                <Input
                  name="office"
                  label="Office"
                  variant="pill"
                  control={formMethods.control}
                  select
                  selectOptions={formOptions.offices}
                  sx={{
                    width: "100%",
                    [theme.breakpoints.up("desktop")]: { width: "50%" },
                  }}
                  removeMarginTop
                />
                <Input
                  name="format"
                  label="Format"
                  variant="pill"
                  control={formMethods.control}
                  select
                  selectOptions={[
                    { label: "All", value: "All" },
                    ...formOptions.eventFormats,
                  ]}
                  sx={{
                    width: "100%",
                    [theme.breakpoints.up("desktop")]: { width: "50%" },
                  }}
                  removeMarginTop
                />
              </Styled.InputStack>
              <Input
                name="eventTags"
                label="Event Tags"
                variant="pill"
                control={formMethods.control}
                select
                multiple
                selectOptions={[
                  { label: "All", value: "All" },
                  ...formOptions.eventTags,
                ]}
                sx={{
                  width: "100%",
                }}
                defaultSelectOption="All"
                removeMarginTop
              />
            </Styled.ModalFilterContainer>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: theme.spacing(1),
              margin: `0 -40px`,
              padding: `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(
                1
              )}`,
              boxShadow: `0px -6px 20px -6px ${theme.colors.action.hoverShadow}`,
            }}
          >
            <Button
              variant="text"
              startIcon={<NotInterestedOutlinedIcon />}
              onClick={() => {
                dispatch(clearSortAndFilterEvents());
                formMethods.reset({
                  sortBy: "date-soonest",
                  format: FormatType.ALL,
                  office: OfficeLocation.NEW_ENGLAND,
                  eventTags: ["All"],
                });
              }}
            >
              Clear All
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                dispatch(
                  filterAndSortEvents({
                    sortBy: formMethods.getValues("sortBy"),
                    format: formMethods.getValues("format"),
                    filters: {
                      format: formMethods.getValues("format") as FormatType,
                      office: formMethods.getValues("office") as OfficeLocation,
                      eventTags: formMethods.getValues("eventTags"),
                    },
                  })
                );
                onClose();
              }}
            >
              Apply
            </Button>
          </Box>
        </FormProvider>
      </Box>
    </BaseModal>
  );
};

export default FilterEventsModal;
